import { useSuggestionSearchBarContext } from '@aibees/elephant/feature/suggestion-search-bar/context';
import { useScrollTracking } from '@aibees/elephant/hooks';
import { Icon, IconButton } from '@aibees/shared/components';
import { useBoolean } from '@aibees/shared/hooks';
import { Box, Button } from '@mui/material';
import { memo, useRef } from 'react';
import * as Styled from './styles';
import * as SX from './sx';
import type { ScrollableAreaProps as Props } from './types';

export const ScrollableArea = memo(({ className, children }: Props) => {
  const { onClearAll, selectedItems } = useSuggestionSearchBarContext();

  const scrollableRef = useRef<HTMLDivElement>(null);
  const hover = useBoolean(false);
  const { canScrollLeft, canScrollRight } = useScrollTracking(
    scrollableRef,
    selectedItems
  );

  const handleScroll = () => {
    if (scrollableRef.current) {
      scrollableRef.current.scrollBy({ left: 211, behavior: 'smooth' });
    }
  };

  const handleScrollLeft = () => {
    if (scrollableRef.current) {
      scrollableRef.current.scrollBy({ left: -211, behavior: 'smooth' });
    }
  };
  const handleMouseLeave = () => {
    hover.onFalse();
  };

  const handleMouseEnter = () => {
    hover.onTrue();
  };

  const handleClearAll = () => {
    hover.onFalse();
    onClearAll();
  };

  return (
    <>
      {canScrollLeft && (
        <Styled.ScrollButtonLeft>
          <IconButton
            icon="chevron-left"
            size="small"
            variant="text"
            sx={SX.scrollBtn(canScrollLeft)}
            onClick={handleScrollLeft}
          />
        </Styled.ScrollButtonLeft>
      )}

      <Styled.ScrollableArea ref={scrollableRef}>
        {children}
      </Styled.ScrollableArea>

      <Styled.ScrollButton
        className="ScrollButton"
        sx={SX.scrollButtonWrapper({
          hover: hover.value,
          length: selectedItems.length,
        })}
      >
        <IconButton
          icon="chevron-right"
          size="small"
          variant="text"
          sx={SX.scrollBtn(canScrollRight)}
          onClick={handleScroll}
        />

        <Box
          sx={SX.removeAll(selectedItems.length)}
          onClick={handleClearAll}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {hover.value && selectedItems.length >= 2 ? (
            <Button variant="outlined" size="xsmall">
              Clear All
            </Button>
          ) : (
            <Icon name="close" size={16} />
          )}
        </Box>
      </Styled.ScrollButton>
    </>
  );
});

ScrollableArea.displayName = 'ScrollableArea';
